import { isValidEmail, PermissionType } from '@assembly-web/services';
import { EyeIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { Button } from '../../../DesignSystem/Inputs/Button';
import type { CriteriaItemProps as Criteria } from './CriteriaItem';
import { CriteriaItem, CriteriaItemLoader } from './CriteriaItem';
import { NonMemberRuleSelector } from './NonMemberRuleSelector';
import {
  type CustomRule,
  type Rule,
  SearchableRulesDropdown,
} from './SearchableRulesDropdown';
import type { PeopleWithAccessProps } from './ShareModal';

const messages = defineMessages({
  peopleWithAccess: {
    defaultMessage: 'People with access',
    id: 'uQGyjD',
  },
  addPeopleOrRulesPlaceholder: {
    defaultMessage: 'Add people or rules placeholder',
    id: '/d1RNY',
  },
  previewList: {
    defaultMessage: 'Preview list',
    id: 'y9y6sj',
  },
});

export function PeopleWithAccess({
  rules,
  members,
  hasNextPageForRules,
  hasNextPageForMembers,
  onLoadMore,
  isLoading,
  isLoadingMembers,
  isFetchingMoreMembers,
  customRules,
  onRulesPermissionChange,
  customRuleOptions,
  onRulesSelectionChange,
  onCustomRulesAction,
  onMemberSearch,
  searchTerm,
  onPreviewButtonClick,
  selectedCustomRule,
  onCustomRuleSelect,
  canInviteEmail,
  handleOnEmailInvite,
  hasUserInvitePermission,
}: Omit<PeopleWithAccessProps, 'view'>) {
  const { formatMessage } = useIntl();

  const itemCount = useMemo(
    () =>
      hasNextPageForRules && rules.length ? rules.length + 1 : rules.length,
    [hasNextPageForRules, rules.length]
  );

  const containerHeight = `${itemCount < 5 ? 60 * itemCount - 1 : 280}px`;

  const customRulesInSearchableRulesDropdown = useMemo(
    () => customRules.map((customRule) => customRule.rule),
    [customRules]
  );

  return (
    <div className="px-2">
      <div className="inline-flex h-fit max-h-12 w-full items-end justify-between gap-11 bg-gray-1 pt-4">
        <TextStyle variant="lg-medium" className="text-gray-9">
          {formatMessage(messages.peopleWithAccess)}
        </TextStyle>
        <Button
          onClick={() => {
            onPreviewButtonClick('previewMembers');
          }}
          variation="tertiaryEmphasized"
          size="small"
          disabled={isLoading}
        >
          <EyeIcon className="h-4 w-4" />
          {formatMessage(messages.previewList)}
        </Button>
      </div>
      <div className="mt-2 flex w-full items-center">
        {selectedCustomRule ? (
          <NonMemberRuleSelector
            ruleType={selectedCustomRule.rule}
            ruleOptions={customRuleOptions}
            onRuleConfirm={({ selectedRules, ruleType, condition }) => {
              onCustomRulesAction({ selectedRules, ruleType, condition });
              onCustomRuleSelect(null);
            }}
            onRuleDelete={() => {
              onCustomRuleSelect(null);
            }}
            displayType={selectedCustomRule.type}
          />
        ) : (
          <SearchableRulesDropdown<Criteria>
            rules={members}
            renderRule={(option: Rule<Criteria>) => {
              return <CriteriaItem {...option.data} />;
            }}
            renderRulesLoader={() => (
              <section className="mt-2 px-2">
                <CriteriaItemLoader showDropdownLoader={false} />
              </section>
            )}
            isLoading={isLoadingMembers}
            isFetchingMoreMembers={isFetchingMoreMembers}
            onRuleSelect={(option: Rule<Criteria>) => {
              onRulesSelectionChange(option.data);
            }}
            customRules={customRulesInSearchableRulesDropdown}
            onCustomRuleSelect={(rule: CustomRule) => {
              const type = customRules.find(
                (customRule) => customRule.rule.id === rule.id
              )?.type;
              if (type) onCustomRuleSelect({ rule, type });
            }}
            hasInvitePermission
            checkIfCurrentEmailCanBeInvited={async (email: string) =>
              isValidEmail(email) || canInviteEmail
            }
            searchTerm={searchTerm}
            onMemberSearch={onMemberSearch}
            hasMoreMembersToFetch={hasNextPageForMembers}
            fetchMoreMembers={onLoadMore}
            contentClassName="w-[573px]"
            handleOnEmailInvite={handleOnEmailInvite}
            hasUserInvitePermission={hasUserInvitePermission}
          />
        )}
      </div>
      {isLoading ? (
        <section className="mt-2">
          <CriteriaItemLoader />
          <CriteriaItemLoader />
        </section>
      ) : (
        <section
          className="mt-2 flex min-h-[60px] flex-col gap-2 overflow-scroll"
          style={{
            height: containerHeight,
          }}
        >
          {rules.map((item) => (
            <CriteriaItem
              key={item.id}
              {...item}
              onPermissionChange={(value) => {
                onRulesPermissionChange(item, value);
              }}
              isDisabled={item.permission?.id === PermissionType.Excluded}
            />
          ))}
        </section>
      )}
    </div>
  );
}
